import {
    handleConversationInitialization,
    handleGetConversationBySid
} from "~/modules/Conversation/ConversationErrorUtils";
import { Reservation, Task } from "~/packages/taskrouter";
import { Conversation } from "~/modules/Conversation";
import { OutboundTaskHandler } from "~/modules/actions/OutboundTaskHandler";
import { ConversationsSdkImpl } from "~/modules/conversations/ConversationsSdkImpl";
import { Action } from "~/modules/actions/Action";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { getLogger, LoggerName } from "~/modules/logger";
import { CbmImpl } from "~/modules/cbm";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { checkRequiredParameter, handleReservationAcceptance } from "~/modules/actions/ActionUtils";
import { sendWorkerTrackingEvent } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { PausedConversation } from "~/modules/actions/Conversation/GetPausedConversations/GetPausedConversations";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";




export interface ResumeConversationResponse {
    task: Task;
    conversation: Conversation;
}








































export class ResumeConversation implements Action<Promise<ResumeConversationResponse>> {
    


    readonly #interactionSid: string;

    


    readonly #channelSid: string;

    constructor(pausedConversation: Pick<PausedConversation, "interactionSid" | "sid">) {
        this.#interactionSid = pausedConversation.interactionSid;
        this.#channelSid = pausedConversation.sid;
    }

    async run(ctx: {}): Promise<ResumeConversationResponse> {
        const context = ctx as ContextManager;
        const logger = getLogger(context)(LoggerName.Actions);

        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const worker = taskRouter.worker;

        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        const conversations = context.retrieveInstanceOf(ContextInstance.ConversationsSdkImpl, ConversationsSdkImpl);

        const conversationInitPromise = handleConversationInitialization(conversations, {
            actionName: "ResumeConversation",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.ResumeConversationCompleted,
            extra: {
                interactionSid: this.#interactionSid,
                channelSid: this.#channelSid
            },
            logger
        });

        logger.debug(
            `ResumeConversation invoked with interactionSid: ${this.#interactionSid} and channelSid: ${
                this.#channelSid
            }`
        );

        checkRequiredParameter(this.#interactionSid, "interactionSid", {
            actionName: "ResumeConversation",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.ResumeConversationCompleted,
            extra: { interactionSid: this.#interactionSid, channelSid: this.#channelSid },
            logger
        });
        checkRequiredParameter(this.#channelSid, "channelSid", {
            actionName: "ResumeConversation",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.ResumeConversationCompleted,
            extra: { interactionSid: this.#interactionSid, channelSid: this.#channelSid },
            logger
        });

        const outboundTaskHandler = new OutboundTaskHandler(worker);
        outboundTaskHandler.startListeningReservations();
        try {
            await cbm.resumeChannel(this.#interactionSid, this.#channelSid, "active");

            sendWorkerTrackingEvent(EVENTS.ResumeConversationCompleted, analytics, worker);
        } catch (error) {
            const errorMsg = `ResumeConversation: Failed to resume conversation for interaction ${
                this.#interactionSid
            } and conversation channel ${this.#channelSid}`;
            logger.error(errorMsg, error);
            sendWorkerTrackingEvent(EVENTS.ResumeConversationCompleted, analytics, worker, {
                error: errorMsg,
                interactionSid: this.#interactionSid,
                channelSid: this.#channelSid
            });
            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.FailedToResumeConversation,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }

        let reservation: Reservation;
        try {
            reservation = await outboundTaskHandler.waitForReservation(
                (r) =>
                    r.task.attributes.flexInteractionSid === this.#interactionSid &&
                    r.task.attributes.flexInteractionChannelSid === this.#channelSid
            );
        } catch (error) {
            const errorMsg = `ResumeConversation: Failed to get reservation after conversation resuming for interaction ${
                this.#interactionSid
            } and channel ${this.#channelSid}`;
            logger.error(errorMsg, error);
            sendWorkerTrackingEvent(EVENTS.ResumeConversationCompleted, analytics, worker, {
                error: errorMsg,
                interactionSid: this.#interactionSid,
                channelSid: this.#channelSid
            });
            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.FailedToGetReservationAfterConversationResuming,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }

        reservation = await handleReservationAcceptance(cbm, reservation, {
            actionName: "ResumeConversation",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.ResumeConversationCompleted,
            extra: { interactionSid: this.#interactionSid, channelSid: this.#channelSid },
            logger
        });

        const conversationSid = reservation.task.attributes.conversationSid as string;

        await conversationInitPromise;
        const conversation = await handleGetConversationBySid(conversations, conversationSid, context, {
            actionName: "ResumeConversation",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.ResumeConversationCompleted,
            extra: { conversationSid },
            logger
        });

        return { task: reservation.task, conversation };
    }
}
